import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import ColourPicker from '../../elements/ColourPicker';
import formatCurrency from '../../../utils/formatCurrency';
import openChatWidget from '../../../utils/openChatWidget';
import useTmsApi from '../../../hooks/useTmsApi';
import Loader from '../../UI/Loader/Loader';
import {
  addToCart,
  updateShowAddToCartTooltip,
} from '../../../redux/reducers/cartReducer';
import useActiveStore from '../../../hooks/useActiveStore';
import { cartItemsSelector } from '../../../redux/selectors/cartSelectors';
import { isKiosk } from '../../../utils/isKiosk';
import AccessoryPromotions from '../AccessoryPromotions/AccessoryPromotions';
import { ProductInventoryResponse } from '../../../types';
import { ContentfulAccessory, EColour, LabelData } from '../../../ContentfulTypes';
import { useFeatureFlag } from '../../FeatureFlag/useFeatureFlag';
import { FeatureFlags } from '../../FeatureFlag/featureFlags';
import {
  VariantContainer,
  VariantDetailsWrapper,
  Headline,
  ProductSku,
  ProductSkuItem,
  ProductSkuBrand,
  Manufacturer,
  Text,
  ContentWrapper,
  PromoWrapper,
  Price,
  PcoPoints,
  StartChatButton,
  LoadingWrapper,
} from './styles';
import VariantCTA from './VariantCTA';
import VariantPriceDetails from './VariantPriceDetails';
import { Variant } from './types';

interface Props {
  contentfulData: ContentfulAccessory;
  variantLabels: LabelData[];
  colourList: EColour[];
  selectedColour?: string;
  setSelectedColour?: (selected: string) => void;
  variantList?: Variant[];
  selectedVariant?: Variant;
  setSelectedVariant?: (selected: Variant) => void;
  inventoryLabels: Record<string, any>;
}

const VariantDetails = ({
  contentfulData,
  variantLabels,
  colourList,
  selectedColour = '',
  setSelectedColour,
  variantList = [],
  selectedVariant,
  setSelectedVariant = () => null,
  inventoryLabels,
}: Props) => {
  const { locale } = useIntl();
  const isKioskMode = isKiosk();
  const { activeStore } = useActiveStore();

  const [errorMessage, setErrorMessage] = useState(false);

  const [payload, setPayload] = useState<Record<string, any> | null>(null);
  const [apiError, setApiError] = useState(false);
  const [inventoryStatus, setInventoryStatus] = useState<string | null>(null);

  const cartItems = useSelector(cartItemsSelector);

  const isReserveAccessoriesFFEnabled = useFeatureFlag(FeatureFlags.ReserveAccessories);

  const quantity = useMemo(
    () => cartItems.find(
      ({ productSku }) => productSku === selectedVariant?.productSku,
    )?.quantity || 0,
    [selectedVariant, cartItems],
  );

  const dispatch = useDispatch();

  useEffect(() => {
    if (selectedVariant) {
      const storeId = activeStore?.childLocationIds?.DEPTMS
        ? activeStore?.childLocationIds?.DEPTMS?.replace(
          /[a-zA-Z-]/g,
          '',
        )?.slice(-5)
        : null;
      const request = [
        {
          storeId,
          productSku: selectedVariant?.productSku,
          productType: 'accessory',
        },
      ];
      setPayload({ request });
    }
  }, [activeStore?.childLocationIds?.DEPTMS, selectedVariant]);

  const { response, error, loading } = useTmsApi<ProductInventoryResponse>(
    '/inventory/product-inventory',
    payload,
  );

  useEffect(() => {
    if (response) {
      setInventoryStatus(
        (response?.response?.[0] as Record<string, any>)?.status,
      );
    }
  }, [response]);

  useEffect(() => {
    if (error) {
      setInventoryStatus('NOT_FOUND');
      setApiError(true);
    }
  }, [error]);

  useEffect(() => setErrorMessage(false), [activeStore]);

  const reserve = () => {
    if (activeStore) {
      // TODO: Add unit test
      if (quantity < 5) {
        dispatch(addToCart(selectedVariant));
        dispatch(updateShowAddToCartTooltip(true));
      } else {
        setErrorMessage(variantLabels?.[6].displayLabel);
      }
    } else {
      setErrorMessage(variantLabels?.[4].displayLabel);
    }
  };

  const selectedVariantFormattedPrice = useMemo(
    () => formatCurrency(selectedVariant?.price, locale),
    [locale, selectedVariant?.price],
  );

  const renderChatButton = () => (!isKioskMode
    && (loading ? (
      <LoadingWrapper>
        <Loader className="small m-auto" />
      </LoadingWrapper>
    ) : (
      <StartChatButton onClick={openChatWidget}>
        {variantLabels?.[3]?.displayLabel}
      </StartChatButton>
    )));

  return (
    <VariantContainer>
      {/** Manufacturer name on desktop screen */}
      {contentfulData.manufacturerName && (
        <Manufacturer data-testid="manufacturer-name">
          {contentfulData.manufacturerName}
        </Manufacturer>
      )}

      {/** Headline on desktop screen */}
      <Headline data-testid="manufacturer-headline">
        {contentfulData?.displayName}
      </Headline>
      {(contentfulData.manufacturerName && selectedVariant) && (
        <ProductSku>
          {contentfulData.manufacturerName && (
            <ProductSkuItem>
              <strong>{variantLabels?.[7].displayLabel}</strong>
              <ProductSkuBrand
                to={`/${locale}/accessories/?brand=${contentfulData.manufacturerName.replace(/\s+/g, '-')}`}
                aria-label={contentfulData.manufacturerName}
              >
                {contentfulData.manufacturerName}
              </ProductSkuBrand>
            </ProductSkuItem>
          )}
          {selectedVariant?.manufacturerModel && (
            <ProductSkuItem>
              <strong>{variantLabels?.[8].displayLabel}</strong>
              {selectedVariant?.manufacturerModel}
            </ProductSkuItem>
          )}
          {selectedVariant?.productSku && (
            <ProductSkuItem>
              <strong>{variantLabels?.[9].displayLabel}</strong>
              {selectedVariant?.productSku?.includes('-') ? variantLabels?.[10].displayLabel : selectedVariant?.productSku}
            </ProductSkuItem>
          )}
        </ProductSku>
      )}
      <Text>{variantLabels?.[0]?.displayLabel}</Text>
      {!!colourList.length && (
        <ColourPicker
          colours={colourList}
          selectedColour={selectedColour}
          onColourChange={setSelectedColour}
          colourSelectable
        />
      )}

      <Text className="option">{variantLabels?.[1]?.displayLabel}</Text>

      {/** Variant details grid */}
      <VariantDetailsWrapper>
        {variantList.map((variant) => (
          <VariantPriceDetails
            variant={variant}
            selectedVariant={selectedVariant}
            setSelectedVariant={setSelectedVariant}
            key={variant.id}
          />
        ))}
      </VariantDetailsWrapper>

      {/** Price and PCO points */}
      <ContentWrapper>
        {selectedVariant?.price && (
          <Price data-testid="item-price">
            {selectedVariantFormattedPrice}
          </Price>
        )}
        {selectedVariant?.pcoPoints && (
          <PcoPoints isKiosk={isKiosk()}>
            <span>{variantLabels?.[2]?.displayLabel}</span>
            <div>
              {selectedVariant.pcoPoints}
              <sup>**</sup>
            </div>
          </PcoPoints>
        )}
      </ContentWrapper>

      <PromoWrapper>
        <AccessoryPromotions
          brand={contentfulData?.manufacturerName}
          type={contentfulData?.type?.value}
          state="text"
        />
      </PromoWrapper>

      {isReserveAccessoriesFFEnabled ? (
        <VariantCTA
          inventoryStatus={inventoryStatus}
          inventoryLabels={inventoryLabels}
          apiError={apiError}
          reserve={reserve}
          variantLabels={variantLabels}
          loading={loading}
          errorMessage={errorMessage}
          openChatWidget={openChatWidget}
        />
      )
        : renderChatButton()}
    </VariantContainer>
  );
};

export default VariantDetails;
